import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { CohortIntegrationFormModal } from '@modules/integrationsV2/components/IntegrationFormModals';
import { useIsIntegrationContextEnabled } from '@modules/integrationsV2/hooks';
import { IntegrationsActionType } from '@modules/integrationsV2/store/integrationsActionType';
import { useIntegrationsStore } from '@modules/integrationsV2/store/integrationsStore';
import {
  getIntegrationFormModalBasedOnChannelType,
  getOAuthRedirectionURL,
  trackIntegrationEvent,
} from '@modules/integrationsV2/utils';
import { getAddIntegrationFormDrawerContent } from '@modules/integrationsV2/utils/getAddIntegrationFormDrawerContent';
import {
  IntegrationChannelViewFragment,
  IntegrationType,
} from 'generated/graphql';
import { useCallback } from 'react';
import { useGetUser } from 'v2/core/services/user';

interface UseOnConnectNewIntegrationOptions {
  integrationChannel: IntegrationChannelViewFragment;
  oAuthURL?: string;
}

export const useOnConnectNewIntegration = ({
  integrationChannel,
  oAuthURL,
}: UseOnConnectNewIntegrationOptions) => {
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();

  const isIntegrationContextEnabled = useIsIntegrationContextEnabled();

  const { openModal } = useMuiModalContext();

  const dispatch = useIntegrationsStore(({ dispatch }) => dispatch);

  const onConnect = useCallback(
    (integrationType: IntegrationType) => {
      if (integrationType === IntegrationType.Cohorts) {
        openModal({
          contentComponent: () => (
            <CohortIntegrationFormModal channelType={integrationChannel.type} />
          ),
          modalProps: {
            fullWidth: true,
            maxWidth: 'lg',
            PaperProps: {
              elevation: 3,
            },
          },
        });
        trackIntegrationEvent({
          eventName: 'Connect Integration CTA Clicked',
          integrationChannelType: integrationChannel.type,
        });
        return;
      }

      const drawerContentComponent = getAddIntegrationFormDrawerContent({
        integrationChannel,
        integrationType,
        onDrawerClose: () => {}, // Not required, because we are not gonna use it
      });

      if (drawerContentComponent && isIntegrationContextEnabled) {
        dispatch({
          type: IntegrationsActionType.SET_VIEW,
          payload: {
            selectedIntegrationFormToConnect: {
              integrationChannel,
              integrationType,
            },
            isAddDrawerOpen: false,
          },
        });
        trackIntegrationEvent({
          eventName: 'Connect Integration CTA Clicked',
          integrationChannelType: integrationChannel.type,
        });
        return;
      }

      const modalComponent = getIntegrationFormModalBasedOnChannelType({
        channelType: integrationChannel.type,
        integrationType,
      });

      // If the integrations requires a form submission
      if (modalComponent && !isIntegrationContextEnabled) {
        openModal({
          contentComponent: () => {
            return <>{modalComponent}</>;
          },
          modalProps: {
            fullWidth: true,
            maxWidth: 'md',
            PaperProps: {
              elevation: 3,
            },
          },
        });
        trackIntegrationEvent({
          eventName: 'Connect Integration CTA Clicked',
          integrationChannelType: integrationChannel.type,
        });
        return;
      }

      // Lastly, we rely on redirecting to OAuth URL
      if (oAuthURL) {
        const redirectURL = getOAuthRedirectionURL({
          channelType: integrationChannel.type,
          oAuthURL,
          tenantSlug,
          integrationType,
        });

        window.open(redirectURL, '_self');
        trackIntegrationEvent({
          eventName: 'Connect Integration CTA Clicked',
          integrationChannelType: integrationChannel.type,
        });
      }
    },
    [
      dispatch,
      integrationChannel,
      isIntegrationContextEnabled,
      oAuthURL,
      openModal,
      tenantSlug,
    ],
  );

  return onConnect;
};
